import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Pen, RotateCcw, Send, Loader2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const translations = {
  it: {
    pageTitle: "Iscrizione Diretta",
    pageSubtitle: "Move Sardinia A.S.D. - Sardinia Kite School",
    step1Title: "Dati Personali",
    step2Title: "Dichiarazioni e Firma",
    name: "Nome",
    surname: "Cognome",
    email: "Email",
    phone: "Telefono",
    fiscalCode: "Codice Fiscale / ID",
    birthDate: "Data di Nascita",
    birthPlace: "Luogo di Nascita",
    residenceCity: "Citta' di Residenza",
    residenceAddress: "Indirizzo",
    declarationsTitle: "Dichiarazioni Obbligatorie",
    insuranceNotice: "⚠️ ASSICURAZIONE ATTREZZATURA NON COMPRESA. In caso di danno, la riparazione parte da €100. Se volete stipulare l'assicurazione, dovete PAGARE IN ANTICIPO la lezione + assicurazione SAFETY TOOL VDWS direttamente alla scuola: costa €39 per 1 anno.",
    healthDeclaration: "Dichiaro di essere in buone condizioni fisiche e di non presentare controindicazioni alla pratica sportiva non agonistica.",
    regulationsDeclaration: "Dichiaro di conoscere e accettare il regolamento dell'associazione Move Sardinia A.S.D.",
    readRegulations: "Leggi il regolamento completo",
    liabilityDeclaration: "Sollevo l'ASD Move Sardinia da ogni responsabilita' per danni a terzi o a me stesso durante la pratica dell'attivita'.",
    privacyDeclaration: "Acconsento al trattamento dei dati personali ai sensi del D.Lgs 196/2003 e GDPR.",
    optionalTitle: "Consensi Opzionali (non obbligatori)",
    mediaConsent: "Acconsento ad essere ripreso/fotografato e che le immagini possano essere utilizzate sui canali social e web della scuola.",
    newsletterConsent: "Desidero ricevere newsletter e aggiornamenti dalla scuola.",
    signTitle: "Firma il Modulo",
    signatureInstructions: "Usa il dito o il mouse per firmare nel riquadro sottostante.",
    signHere: "Firma qui",
    clearSignature: "Cancella Firma",
    signatureNote: "Nota: Firmando questo modulo accetti tutte le condizioni indicate e confermi la veridicita' dei dati inseriti.",
    membershipFee: "Quota associativa €10 inclusa",
    back: "Indietro",
    next: "Avanti",
    submit: "Invia Iscrizione",
    sending: "Invio...",
    successTitle: "Iscrizione Completata!",
    successMessage: "La tua iscrizione e' stata registrata con successo.",
    errorRequired: "Compila tutti i campi obbligatori (Nome, Cognome, Email, Telefono)",
    errorDeclarations: "Devi accettare tutte le dichiarazioni obbligatorie",
    errorSignature: "Firma il modulo per procedere",
    newRegistration: "Nuova Iscrizione",
    minorReminder: "Per i minorenni: inserire i dati del figlio/a e la firma del genitore.",
    previewTitle: "Anteprima Modulo",
    previewDesc: "Verifica i dati inseriti prima di confermare l'iscrizione.",
    loadingPdf: "Generazione anteprima...",
    confirmSubmit: "Conferma e Invia",
    editData: "Modifica Dati",
    rentalNoticeTitle: "⚠️ INFORMAZIONI IMPORTANTI PER NOLEGGIO",
    rentalNoticeBody: "In caso stai noleggiando, leggi attentamente. Se la persona non riesce ad andare in acqua viene comunque considerato un minimo di 30 minuti di noleggio. Per il noleggio è obbligatorio lasciare un documento d'identità o una borsa. L'attrezzatura è sotto la propria responsabilità: in caso di danno consigliamo di stipulare l'assicurazione SAFETY TOOL VDWS che copre le attrezzature noleggiate da scuole (€39 all'anno). Il noleggio kite si effettua in mare, non in laguna; il noleggio Wing è disponibile sia in laguna che in mare. Livello richiesto per il noleggio: bolina e transizioni.",
    changeLanguage: "Cambia lingua",
  },
  en: {
    pageTitle: "Direct Registration",
    pageSubtitle: "Move Sardinia A.S.D. - Sardinia Kite School",
    step1Title: "Personal Data",
    step2Title: "Declarations & Signature",
    name: "First Name",
    surname: "Last Name",
    email: "Email",
    phone: "Phone",
    fiscalCode: "Fiscal Code / ID",
    birthDate: "Date of Birth",
    birthPlace: "Place of Birth",
    residenceCity: "City of Residence",
    residenceAddress: "Address",
    declarationsTitle: "Mandatory Declarations",
    insuranceNotice: "⚠️ EQUIPMENT INSURANCE NOT INCLUDED. Repairs start from €100 in case of damage. If you want insurance, you must PAY IN ADVANCE the lesson + SAFETY TOOL VDWS insurance directly to the school: it costs €39 for 1 year.",
    healthDeclaration: "I declare that I am in good physical condition with no contraindications for non-competitive sports.",
    regulationsDeclaration: "I declare that I know and accept the regulations of Move Sardinia A.S.D.",
    readRegulations: "Read full regulations",
    liabilityDeclaration: "I release ASD Move Sardinia from any liability for damages to third parties or myself during the activity.",
    privacyDeclaration: "I consent to the processing of personal data pursuant to Legislative Decree 196/2003 and GDPR.",
    optionalTitle: "Optional Consents (not mandatory)",
    mediaConsent: "I consent to being filmed/photographed and the images may be used on the school's social and web channels.",
    newsletterConsent: "I wish to receive newsletters and updates from the school.",
    signTitle: "Sign the Form",
    signatureInstructions: "Use your finger or mouse to sign in the box below.",
    signHere: "Sign here",
    clearSignature: "Clear Signature",
    signatureNote: "Note: By signing this form you accept all conditions and confirm the accuracy of the data entered.",
    membershipFee: "Membership fee €10 included",
    back: "Back",
    next: "Next",
    submit: "Submit Registration",
    sending: "Sending...",
    successTitle: "Registration Complete!",
    successMessage: "Your registration has been successfully recorded.",
    errorRequired: "Please fill in all required fields (Name, Surname, Email, Phone)",
    errorDeclarations: "You must accept all mandatory declarations",
    errorSignature: "Please sign the form to proceed",
    newRegistration: "New Registration",
    minorReminder: "For minors: enter the child's data and the parent's signature.",
    previewTitle: "Form Preview",
    previewDesc: "Review your data before confirming the registration.",
    loadingPdf: "Generating preview...",
    confirmSubmit: "Confirm & Submit",
    editData: "Edit Data",
    rentalNoticeTitle: "⚠️ IMPORTANT RENTAL INFORMATION",
    rentalNoticeBody: "If you are renting, please read carefully. If the person is unable to go in the water, a minimum of 30 minutes of rental will still be charged. An ID document or bag is required for the rental. The equipment is under the renter's full responsibility: in case of damage we recommend the SAFETY TOOL VDWS insurance, which covers equipment rented from schools (€39 per year). Kite rental takes place at sea, not in the lagoon; Wing rental is available both in the lagoon and at sea. Required rental level: upwind sailing and transitions.",
    changeLanguage: "Change language",
  },
  fr: {
    pageTitle: "Inscription Directe",
    pageSubtitle: "Move Sardinia A.S.D. - Sardinia Kite School",
    step1Title: "Données Personnelles",
    step2Title: "Déclarations et Signature",
    name: "Prénom",
    surname: "Nom",
    email: "Email",
    phone: "Téléphone",
    fiscalCode: "Code Fiscal / Pièce d'identité",
    birthDate: "Date de Naissance",
    birthPlace: "Lieu de Naissance",
    residenceCity: "Ville de Résidence",
    residenceAddress: "Adresse",
    declarationsTitle: "Déclarations Obligatoires",
    insuranceNotice: "⚠️ ASSURANCE MATÉRIEL NON INCLUSE. La réparation démarre à 100 € en cas de dommage. Pour souscrire l'assurance, vous devez PAYER À L'AVANCE le cours + assurance SAFETY TOOL VDWS directement à l'école : coût 39 € pour 1 an.",
    healthDeclaration: "Je déclare être en bonne condition physique sans contre-indications pour les sports non compétitifs.",
    regulationsDeclaration: "Je déclare connaître et accepter le règlement de Move Sardinia A.S.D.",
    readRegulations: "Lire le règlement complet",
    liabilityDeclaration: "Je libère ASD Move Sardinia de toute responsabilité pour les dommages causés à des tiers ou à moi-même pendant l'activité.",
    privacyDeclaration: "J'accepte le traitement des données personnelles selon le Décret Législatif 196/2003 et le RGPD.",
    optionalTitle: "Consentements Optionnels (non obligatoires)",
    mediaConsent: "J'accepte d'être filmé/photographié et que les images puissent être utilisées sur les canaux sociaux et web de l'école.",
    newsletterConsent: "Je souhaite recevoir des newsletters et des mises à jour de l'école.",
    signTitle: "Signez le Formulaire",
    signatureInstructions: "Utilisez votre doigt ou la souris pour signer dans le cadre ci-dessous.",
    signHere: "Signez ici",
    clearSignature: "Effacer la Signature",
    signatureNote: "Note: En signant ce formulaire, vous acceptez toutes les conditions et confirmez l'exactitude des données saisies.",
    membershipFee: "Cotisation d'adhésion 10 € incluse",
    back: "Retour",
    next: "Suivant",
    submit: "Envoyer l'Inscription",
    sending: "Envoi...",
    successTitle: "Inscription Complète!",
    successMessage: "Votre inscription a été enregistrée avec succès.",
    errorRequired: "Veuillez remplir tous les champs obligatoires (Prénom, Nom, Email, Téléphone)",
    errorDeclarations: "Vous devez accepter toutes les déclarations obligatoires",
    errorSignature: "Veuillez signer le formulaire pour continuer",
    newRegistration: "Nouvelle Inscription",
    minorReminder: "Pour les mineurs: saisissez les données de l'enfant et la signature du parent.",
    previewTitle: "Aperçu du Formulaire",
    previewDesc: "Vérifiez vos données avant de confirmer l'inscription.",
    loadingPdf: "Génération de l'aperçu...",
    confirmSubmit: "Confirmer et Envoyer",
    editData: "Modifier les Données",
    rentalNoticeTitle: "⚠️ INFORMATIONS IMPORTANTES POUR LA LOCATION",
    rentalNoticeBody: "Si vous louez, veuillez lire attentivement. Si la personne ne peut pas aller à l'eau, un minimum de 30 minutes de location sera quand même facturé. Pour la location, il est obligatoire de laisser une pièce d'identité ou un sac. L'équipement est sous la responsabilité du locataire: en cas de dommage, nous recommandons l'assurance SAFETY TOOL VDWS qui couvre les équipements loués dans les écoles (39 € par an). La location de kite se fait en mer, pas dans la lagune; la location Wing est disponible aussi bien dans la lagune qu'en mer. Niveau requis pour la location: remontée au près et transitions.",
    changeLanguage: "Changer de langue",
  },
  de: {
    pageTitle: "Direktanmeldung",
    pageSubtitle: "Move Sardinia A.S.D. - Sardinia Kite School",
    step1Title: "Persönliche Daten",
    step2Title: "Erklärungen und Unterschrift",
    name: "Vorname",
    surname: "Nachname",
    email: "E-Mail",
    phone: "Telefon",
    fiscalCode: "Steuernummer / Ausweis",
    birthDate: "Geburtsdatum",
    birthPlace: "Geburtsort",
    residenceCity: "Wohnort",
    residenceAddress: "Adresse",
    declarationsTitle: "Pflichterklärungen",
    insuranceNotice: "⚠️ AUSRÜSTUNGSVERSICHERUNG NICHT ENTHALTEN. Reparaturen beginnen bei 100 € im Schadensfall. Wer eine Versicherung möchte, muss den Kurs + SAFETY TOOL VDWS-Versicherung IM VORAUS direkt an die Schule ZAHLEN: 39 € für 1 Jahr.",
    healthDeclaration: "Ich erkläre, dass ich körperlich fit bin und keine Kontraindikationen für nicht-wettkampforientierte Sportarten habe.",
    regulationsDeclaration: "Ich erkläre, dass ich die Vorschriften von Move Sardinia A.S.D. kenne und akzeptiere.",
    readRegulations: "Vollständige Vorschriften lesen",
    liabilityDeclaration: "Ich entbinde ASD Move Sardinia von jeder Haftung für Schäden an Dritten oder an mir selbst während der Aktivität.",
    privacyDeclaration: "Ich stimme der Verarbeitung personenbezogener Daten gemäß Gesetzesdekret 196/2003 und DSGVO zu.",
    optionalTitle: "Optionale Zustimmungen (nicht obligatorisch)",
    mediaConsent: "Ich stimme zu, gefilmt/fotografiert zu werden und dass die Bilder in den sozialen und Web-Kanälen der Schule verwendet werden können.",
    newsletterConsent: "Ich möchte Newsletter und Updates der Schule erhalten.",
    signTitle: "Formular unterschreiben",
    signatureInstructions: "Benutzen Sie Ihren Finger oder die Maus, um im Feld unten zu unterschreiben.",
    signHere: "Hier unterschreiben",
    clearSignature: "Unterschrift löschen",
    signatureNote: "Hinweis: Mit Ihrer Unterschrift akzeptieren Sie alle angegebenen Bedingungen und bestätigen die Richtigkeit der eingegebenen Daten.",
    membershipFee: "Mitgliedsbeitrag 10 € inbegriffen",
    back: "Zurück",
    next: "Weiter",
    submit: "Anmeldung Senden",
    sending: "Senden...",
    successTitle: "Anmeldung Abgeschlossen!",
    successMessage: "Ihre Anmeldung wurde erfolgreich registriert.",
    errorRequired: "Bitte füllen Sie alle Pflichtfelder aus (Vorname, Nachname, E-Mail, Telefon)",
    errorDeclarations: "Sie müssen alle Pflichterklärungen akzeptieren",
    errorSignature: "Bitte unterschreiben Sie das Formular, um fortzufahren",
    newRegistration: "Neue Anmeldung",
    minorReminder: "Für Minderjährige: Daten des Kindes und Unterschrift des Elternteils eingeben.",
    previewTitle: "Formularvorschau",
    previewDesc: "Überprüfen Sie Ihre Daten vor der Bestätigung der Anmeldung.",
    loadingPdf: "Vorschau wird erstellt...",
    confirmSubmit: "Bestätigen & Senden",
    editData: "Daten Bearbeiten",
    rentalNoticeTitle: "⚠️ WICHTIGE INFORMATIONEN ZUM VERLEIH",
    rentalNoticeBody: "Wenn Sie mieten, lesen Sie bitte aufmerksam. Wenn die Person nicht ins Wasser gehen kann, werden trotzdem mindestens 30 Minuten Verleih berechnet. Für den Verleih ist es Pflicht, ein Ausweisdokument oder eine Tasche zu hinterlassen. Die Ausrüstung liegt in der eigenen Verantwortung: Im Schadensfall empfehlen wir die SAFETY TOOL VDWS-Versicherung, die von Schulen geliehene Ausrüstung abdeckt (39 € pro Jahr). Der Kite-Verleih findet im Meer statt, nicht in der Lagune; der Wing-Verleih ist sowohl in der Lagune als auch im Meer verfügbar. Erforderliches Niveau für den Verleih: Höhe halten und Halsen.",
    changeLanguage: "Sprache ändern",
  },
  es: {
    pageTitle: "Inscripción Directa",
    pageSubtitle: "Move Sardinia A.S.D. - Sardinia Kite School",
    step1Title: "Datos Personales",
    step2Title: "Declaraciones y Firma",
    name: "Nombre",
    surname: "Apellido",
    email: "Email",
    phone: "Teléfono",
    fiscalCode: "Código Fiscal / DNI",
    birthDate: "Fecha de Nacimiento",
    birthPlace: "Lugar de Nacimiento",
    residenceCity: "Ciudad de Residencia",
    residenceAddress: "Dirección",
    declarationsTitle: "Declaraciones Obligatorias",
    insuranceNotice: "⚠️ SEGURO DEL EQUIPO NO INCLUIDO. La reparación empieza desde 100 € en caso de daño. Si deseas el seguro, debes PAGAR POR ADELANTADO la clase + seguro SAFETY TOOL VDWS directamente a la escuela: cuesta 39 € por 1 año.",
    healthDeclaration: "Declaro estar en buenas condiciones físicas sin contraindicaciones para deportes no competitivos.",
    regulationsDeclaration: "Declaro conocer y aceptar el reglamento de Move Sardinia A.S.D.",
    readRegulations: "Leer el reglamento completo",
    liabilityDeclaration: "Eximo a ASD Move Sardinia de cualquier responsabilidad por daños a terceros o a mí mismo durante la actividad.",
    privacyDeclaration: "Doy mi consentimiento para el tratamiento de datos personales según el Decreto Legislativo 196/2003 y el RGPD.",
    optionalTitle: "Consentimientos Opcionales (no obligatorios)",
    mediaConsent: "Doy mi consentimiento para ser grabado/fotografiado y que las imágenes puedan usarse en los canales sociales y web de la escuela.",
    newsletterConsent: "Deseo recibir boletines y actualizaciones de la escuela.",
    signTitle: "Firmar el Formulario",
    signatureInstructions: "Usa el dedo o el ratón para firmar en el cuadro de abajo.",
    signHere: "Firma aquí",
    clearSignature: "Borrar Firma",
    signatureNote: "Nota: Al firmar este formulario, aceptas todas las condiciones indicadas y confirmas la veracidad de los datos introducidos.",
    membershipFee: "Cuota de afiliación 10 € incluida",
    back: "Atrás",
    next: "Siguiente",
    submit: "Enviar Inscripción",
    sending: "Enviando...",
    successTitle: "¡Inscripción Completada!",
    successMessage: "Tu inscripción se ha registrado con éxito.",
    errorRequired: "Por favor, completa todos los campos obligatorios (Nombre, Apellido, Email, Teléfono)",
    errorDeclarations: "Debes aceptar todas las declaraciones obligatorias",
    errorSignature: "Por favor, firma el formulario para continuar",
    newRegistration: "Nueva Inscripción",
    minorReminder: "Para menores: introducir los datos del hijo/a y la firma del padre/madre.",
    previewTitle: "Vista Previa del Formulario",
    previewDesc: "Revisa tus datos antes de confirmar la inscripción.",
    loadingPdf: "Generando vista previa...",
    confirmSubmit: "Confirmar y Enviar",
    editData: "Editar Datos",
    rentalNoticeTitle: "⚠️ INFORMACIÓN IMPORTANTE PARA EL ALQUILER",
    rentalNoticeBody: "Si estás alquilando, lee atentamente. Si la persona no puede entrar al agua, se considerará igualmente un mínimo de 30 minutos de alquiler. Para el alquiler es obligatorio dejar un documento de identidad o un bolso. El equipo está bajo la propia responsabilidad: en caso de daño recomendamos contratar el seguro SAFETY TOOL VDWS que cubre los equipos alquilados de escuelas (39 € al año). El alquiler de kite se realiza en el mar, no en la laguna; el alquiler Wing está disponible tanto en la laguna como en el mar. Nivel requerido para el alquiler: ceñida y transiciones.",
    changeLanguage: "Cambiar idioma",
  }
};

const regulationsContent = {
  it: {
    title: "Regolamento Sardinia Kite School",
    rules: [
      { number: 1, text: "Per le lezioni sei tenuto ad arrivare 15 minuti prima della lezione per iniziare in orario." },
      { number: 2, text: "La lezione inizia quando l'attrezzatura viene data." },
      { number: 3, text: "La cancellazione o lo spostamento della lezione deve essere comunicato ENTRO LE ORE 18:00 DEL GIORNO PRIMA." },
      { number: 4, text: "Le lezioni SEMI-PRIVATE sono con 1 attrezzatura alternata tra i partecipanti. Le lezioni di GRUPPO sono con piu' attrezzature." },
      { number: 5, text: "L'istruttore viene SCELTO DALLA SCUOLA in base alla disponibilita'." },
      { number: 6, text: "ASSICURAZIONE ATTREZZATURA NON COMPRESA. In caso di danno all'attrezzatura, la riparazione parte da un minimo di €100. Chi desidera un'assicurazione puo' sottoscriverla autonomamente con SAFETY TOOL VDWS direttamente dal loro sito (safety-tool-vdws.com)." }
    ],
    close: "Chiudi"
  },
  en: {
    title: "Sardinia Kite School Regulations",
    rules: [
      { number: 1, text: "You must arrive 15 minutes before the lesson to start on time." },
      { number: 2, text: "The lesson starts when the equipment is given." },
      { number: 3, text: "Cancellation must be communicated BY 6:00 PM THE DAY BEFORE." },
      { number: 4, text: "SEMI-PRIVATE lessons share 1 equipment. GROUP lessons have individual equipment." },
      { number: 5, text: "The instructor is CHOSEN BY THE SCHOOL based on availability." },
      { number: 6, text: "EQUIPMENT INSURANCE NOT INCLUDED. In case of damage, repairs start from a minimum of €100. Those who want insurance can subscribe on their own with SAFETY TOOL VDWS directly on their website (safety-tool-vdws.com)." }
    ],
    close: "Close"
  },
  fr: {
    title: "Règlement Sardinia Kite School",
    rules: [
      { number: 1, text: "Vous devez arriver 15 minutes avant le cours pour commencer à l'heure." },
      { number: 2, text: "Le cours commence dès que l'équipement est remis." },
      { number: 3, text: "L'annulation ou le déplacement du cours doit être communiqué AVANT 18H00 LA VEILLE." },
      { number: 4, text: "Les cours SEMI-PRIVÉS utilisent 1 équipement alterné entre participants. Les cours de GROUPE ont plusieurs équipements." },
      { number: 5, text: "L'instructeur est CHOISI PAR L'ÉCOLE selon la disponibilité." },
      { number: 6, text: "ASSURANCE MATÉRIEL NON INCLUSE. En cas de dommage, la réparation démarre à un minimum de 100 €. Si vous souhaitez une assurance, souscrivez-la de manière autonome avec SAFETY TOOL VDWS directement sur leur site (safety-tool-vdws.com)." }
    ],
    close: "Fermer"
  },
  de: {
    title: "Vorschriften Sardinia Kite School",
    rules: [
      { number: 1, text: "Sie müssen 15 Minuten vor dem Kurs ankommen, um pünktlich zu beginnen." },
      { number: 2, text: "Der Kurs beginnt, wenn die Ausrüstung übergeben wird." },
      { number: 3, text: "Stornierung oder Verschiebung muss BIS 18:00 UHR AM VORTAG mitgeteilt werden." },
      { number: 4, text: "HALB-PRIVATE Kurse teilen sich 1 Ausrüstung. GRUPPEN-Kurse haben mehrere Ausrüstungen." },
      { number: 5, text: "Der Lehrer wird VON DER SCHULE nach Verfügbarkeit AUSGEWÄHLT." },
      { number: 6, text: "AUSRÜSTUNGSVERSICHERUNG NICHT ENTHALTEN. Im Schadensfall beginnt die Reparatur bei mindestens 100 €. Wer eine Versicherung wünscht, kann diese selbstständig bei SAFETY TOOL VDWS direkt auf deren Website (safety-tool-vdws.com) abschließen." }
    ],
    close: "Schließen"
  },
  es: {
    title: "Reglamento Sardinia Kite School",
    rules: [
      { number: 1, text: "Debes llegar 15 minutos antes de la clase para empezar a tiempo." },
      { number: 2, text: "La clase empieza cuando se entrega el equipo." },
      { number: 3, text: "La cancelación o el cambio de clase debe comunicarse ANTES DE LAS 18:00 DEL DÍA ANTERIOR." },
      { number: 4, text: "Las clases SEMI-PRIVADAS comparten 1 equipo. Las clases de GRUPO tienen varios equipos." },
      { number: 5, text: "El instructor lo ELIGE LA ESCUELA según la disponibilidad." },
      { number: 6, text: "SEGURO DEL EQUIPO NO INCLUIDO. En caso de daño, la reparación empieza desde un mínimo de 100 €. Quien desee un seguro puede contratarlo por su cuenta con SAFETY TOOL VDWS directamente en su sitio (safety-tool-vdws.com)." }
    ],
    close: "Cerrar"
  }
};

const previewLabels = {
  it: { health: "Stato di salute", regulations: "Regolamento", liability: "Scarico responsabilita'", privacy: "Privacy", media: "Consenso media", newsletter: "Newsletter", signature: "Firma:", signaturePlaceholder: "(da compilare sotto)" },
  en: { health: "Health declaration", regulations: "Regulations", liability: "Liability waiver", privacy: "Privacy", media: "Media consent", newsletter: "Newsletter", signature: "Signature:", signaturePlaceholder: "(to be signed below)" },
  fr: { health: "État de santé", regulations: "Règlement", liability: "Décharge de responsabilité", privacy: "Confidentialité", media: "Consentement média", newsletter: "Newsletter", signature: "Signature:", signaturePlaceholder: "(à signer ci-dessous)" },
  de: { health: "Gesundheitszustand", regulations: "Vorschriften", liability: "Haftungsausschluss", privacy: "Datenschutz", media: "Medien-Zustimmung", newsletter: "Newsletter", signature: "Unterschrift:", signaturePlaceholder: "(unten zu unterschreiben)" },
  es: { health: "Estado de salud", regulations: "Reglamento", liability: "Exoneración de responsabilidad", privacy: "Privacidad", media: "Consentimiento media", newsletter: "Newsletter", signature: "Firma:", signaturePlaceholder: "(firmar abajo)" },
};

export default function MemberPage() {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showRegulations, setShowRegulations] = useState(false);
  const [language, setLanguage] = useState("it");
  const [languageManuallySet, setLanguageManuallySet] = useState(false);

  // Country code -> language code
  const COUNTRY_TO_LANG = {
    IT: 'it',
    GB: 'en', US: 'en', IE: 'en', AU: 'en', NZ: 'en', CA: 'en', MT: 'en', IN: 'en',
    FR: 'fr', BE: 'fr', LU: 'fr', CH: 'fr', MC: 'fr',
    DE: 'de', AT: 'de',
    ES: 'es', MX: 'es', AR: 'es', CL: 'es', CO: 'es', PE: 'es', VE: 'es', UY: 'es',
  };

  // Auto-detect language from IP on first load (unless user already selected manually)
  useEffect(() => {
    let cancelled = false;
    const detect = async () => {
      try {
        const res = await fetch('https://ipapi.co/json/');
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled || !data?.country_code || languageManuallySet) return;
        const detected = COUNTRY_TO_LANG[data.country_code] || 'en';
        setLanguage(detected);
      } catch (_) { /* ignore */ }
    };
    detect();
    return () => { cancelled = true; };
  }, []);

  const onLanguageChange = (newLang) => {
    setLanguageManuallySet(true);
    setLanguage(newLang);
  };

  const [formData, setFormData] = useState({
    name: "", surname: "", fiscal_code: "", birth_date: "", birth_place: "",
    residence_city: "", residence_address: "", phone: "", email: "",
    accepts_health_declaration: false, accepts_regulations: false,
    accepts_liability_waiver: false, accepts_privacy: false,
    accepts_media_consent: false, accepts_newsletter: false,
    signature_data: "",
  });

  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  const t = translations[language] || translations.it;
  const regs = regulationsContent[language] || regulationsContent.it;

  useEffect(() => {
    if (step === 3 && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      ctx.strokeStyle = "#1a1a1a";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
    }
  }, [step]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return { x: (e.touches[0].clientX - rect.left) * scaleX, y: (e.touches[0].clientY - rect.top) * scaleY };
    }
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const handleSubmit = async () => {
    if (!hasSignature) {
      toast.error(t.errorSignature);
      return;
    }
    setIsSubmitting(true);
    try {
      const signatureData = canvasRef.current.toDataURL("image/png");
      await axios.post(`${API}/member/register`, {
        ...formData,
        signature_data: signatureData,
        language,
      });
      setSubmitted(true);
      toast.success(t.successMessage);
    } catch (err) {
      toast.error("Errore nell'invio. Riprova.");
    }
    setIsSubmitting(false);
  };

  const goToStep3 = () => {
    if (!formData.accepts_health_declaration || !formData.accepts_regulations || !formData.accepts_liability_waiver || !formData.accepts_privacy) {
      toast.error(t.errorDeclarations);
      return;
    }
    setStep(3);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center"
        >
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-testid="member-success-title">{t.successTitle}</h2>
          <p className="text-gray-600 mb-6">{t.successMessage}</p>
          <Button
            onClick={() => { setSubmitted(false); setStep(1); setHasSignature(false); setFormData({
              name: "", surname: "", fiscal_code: "", birth_date: "", birth_place: "",
              residence_city: "", residence_address: "", phone: "", email: "",
              accepts_health_declaration: false, accepts_regulations: false,
              accepts_liability_waiver: false, accepts_privacy: false,
              accepts_media_consent: false, accepts_newsletter: false,
              signature_data: "",
            }); }}
            className="bg-teal-500 hover:bg-teal-600"
            data-testid="member-new-registration-btn"
          >
            {t.newRegistration}
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="bg-white w-full max-w-lg rounded-2xl shadow-xl overflow-hidden"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white p-5">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold" data-testid="member-page-title">{t.pageTitle}</h1>
              <p className="text-sm text-white/80">{t.pageSubtitle}</p>
            </div>
            <div className="flex flex-col items-end gap-1">
              <span className="text-[10px] uppercase tracking-wide text-white/80 font-medium" data-testid="member-lang-label">{t.changeLanguage}</span>
              <select
                value={language}
                onChange={(e) => onLanguageChange(e.target.value)}
                className="bg-white/20 text-white border-0 rounded-lg px-2 py-1 text-sm cursor-pointer"
                data-testid="member-lang-select"
              >
                <option value="it" className="text-gray-900">🇮🇹 IT</option>
                <option value="en" className="text-gray-900">🇬🇧 EN</option>
                <option value="fr" className="text-gray-900">🇫🇷 FR</option>
                <option value="de" className="text-gray-900">🇩🇪 DE</option>
                <option value="es" className="text-gray-900">🇪🇸 ES</option>
              </select>
            </div>
          </div>
        </div>

        {/* Progress */}
        <div className="flex gap-2 p-4 bg-gray-50">
          {[1, 2, 3].map(s => (
            <div key={s} className="flex-1 h-2 rounded-full overflow-hidden bg-gray-200">
              <div
                className={`h-full transition-all duration-300 ${s <= step ? "bg-teal-500" : "bg-gray-200"}`}
                style={{ width: s <= step ? "100%" : "0%" }}
              />
            </div>
          ))}
        </div>

        {/* Form */}
        <div className="p-5 space-y-4 max-h-[65vh] overflow-y-auto">
          {step === 1 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
              <h3 className="font-semibold text-gray-900">{t.step1Title}</h3>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t.name} *</Label>
                  <Input value={formData.name} onChange={e => handleChange("name", e.target.value)} placeholder="Mario" data-testid="member-name" />
                </div>
                <div>
                  <Label>{t.surname} *</Label>
                  <Input value={formData.surname} onChange={e => handleChange("surname", e.target.value)} placeholder="Rossi" data-testid="member-surname" />
                </div>
              </div>

              <div>
                <Label>{t.email} *</Label>
                <Input type="email" value={formData.email} onChange={e => handleChange("email", e.target.value)} placeholder="mario@email.com" data-testid="member-email" />
              </div>

              <div>
                <Label>{t.phone} *</Label>
                <Input type="tel" value={formData.phone} onChange={e => handleChange("phone", e.target.value)} placeholder="+39 123 456 7890" data-testid="member-phone" />
              </div>

              <div>
                <Label>{t.fiscalCode}</Label>
                <Input value={formData.fiscal_code} onChange={e => handleChange("fiscal_code", e.target.value)} placeholder="RSSMRA80A01H501U" data-testid="member-fiscal" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t.birthDate}</Label>
                  <Input type="date" value={formData.birth_date} onChange={e => handleChange("birth_date", e.target.value)} data-testid="member-birthdate" />
                </div>
                <div>
                  <Label>{t.birthPlace}</Label>
                  <Input value={formData.birth_place} onChange={e => handleChange("birth_place", e.target.value)} placeholder="Roma" data-testid="member-birthplace" />
                </div>
              </div>

              <div>
                <Label>{t.residenceCity}</Label>
                <Input value={formData.residence_city} onChange={e => handleChange("residence_city", e.target.value)} placeholder="Olbia" data-testid="member-city" />
              </div>

              <div>
                <Label>{t.residenceAddress}</Label>
                <Input value={formData.residence_address} onChange={e => handleChange("residence_address", e.target.value)} placeholder="Via Roma 1" data-testid="member-address" />
              </div>

              <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg text-sm text-amber-800">
                {t.minorReminder}
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
              <h3 className="font-semibold text-gray-900">{t.declarationsTitle}</h3>

              <div className="p-3 bg-amber-50 border-2 border-amber-400 rounded-lg" data-testid="member-insurance-notice">
                <p className="text-xs text-amber-900 font-medium leading-relaxed">{t.insuranceNotice}</p>
              </div>

              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <Checkbox id="m-health" checked={formData.accepts_health_declaration} onCheckedChange={c => handleChange("accepts_health_declaration", c)} />
                  <label htmlFor="m-health" className="text-sm text-gray-700 cursor-pointer">{t.healthDeclaration}</label>
                </div>

                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <Checkbox id="m-regulations" checked={formData.accepts_regulations} onCheckedChange={c => handleChange("accepts_regulations", c)} className="mt-0.5" />
                  <div className="flex-1">
                    <label htmlFor="m-regulations" className="text-sm text-gray-700 cursor-pointer">{t.regulationsDeclaration}</label>
                    <button type="button" onClick={() => setShowRegulations(true)} className="flex items-center gap-1 mt-1 text-xs text-teal-600 hover:text-teal-700 font-medium">
                      <ExternalLink className="w-3 h-3" />
                      {t.readRegulations}
                    </button>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <Checkbox id="m-liability" checked={formData.accepts_liability_waiver} onCheckedChange={c => handleChange("accepts_liability_waiver", c)} />
                  <label htmlFor="m-liability" className="text-sm text-gray-700 cursor-pointer">{t.liabilityDeclaration}</label>
                </div>

                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <Checkbox id="m-privacy" checked={formData.accepts_privacy} onCheckedChange={c => handleChange("accepts_privacy", c)} />
                  <label htmlFor="m-privacy" className="text-sm text-gray-700 cursor-pointer">{t.privacyDeclaration}</label>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-800 mb-3">{t.optionalTitle}</h4>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Checkbox id="m-media" checked={formData.accepts_media_consent} onCheckedChange={c => handleChange("accepts_media_consent", c)} />
                    <label htmlFor="m-media" className="text-sm text-blue-700 cursor-pointer">{t.mediaConsent}</label>
                  </div>
                  <div className="flex items-start gap-3">
                    <Checkbox id="m-newsletter" checked={formData.accepts_newsletter} onCheckedChange={c => handleChange("accepts_newsletter", c)} />
                    <label htmlFor="m-newsletter" className="text-sm text-blue-700 cursor-pointer">{t.newsletterConsent}</label>
                  </div>
                </div>
              </div>

              <div className="bg-teal-50 p-3 rounded-lg text-sm text-teal-800">
                <strong>{t.membershipFee}</strong>
              </div>

              <div className="bg-amber-50 border-2 border-amber-300 p-4 rounded-lg" data-testid="member-rental-notice">
                <h4 className="font-bold text-amber-900 mb-2 text-sm">{t.rentalNoticeTitle}</h4>
                <p className="text-xs text-amber-900 leading-relaxed whitespace-pre-line">{t.rentalNoticeBody}</p>
              </div>
            </motion.div>
          )}

          {/* Step 3: Inline Preview + Signature */}
          {step === 3 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
              <h3 className="font-semibold text-gray-900" data-testid="member-preview-title">{t.previewTitle}</h3>
              <p className="text-sm text-gray-600">{t.previewDesc}</p>

              {/* Inline document preview */}
              <div className="border border-gray-300 rounded-lg bg-white p-4 shadow-inner" data-testid="member-inline-preview">
                <div className="text-center border-b pb-3 mb-3">
                  <p className="text-xs font-bold text-gray-800 uppercase tracking-wide">Move Sardinia A.S.D.</p>
                  <p className="text-[10px] text-gray-500">Modulo di Iscrizione</p>
                </div>

                <div className="space-y-2 text-xs text-gray-700">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                    <div><span className="font-semibold">{t.name}:</span> {formData.name}</div>
                    <div><span className="font-semibold">{t.surname}:</span> {formData.surname}</div>
                    <div><span className="font-semibold">{t.email}:</span> {formData.email}</div>
                    <div><span className="font-semibold">{t.phone}:</span> {formData.phone}</div>
                    {formData.fiscal_code && <div className="col-span-2"><span className="font-semibold">{t.fiscalCode}:</span> {formData.fiscal_code}</div>}
                    {formData.birth_date && <div><span className="font-semibold">{t.birthDate}:</span> {formData.birth_date}</div>}
                    {formData.birth_place && <div><span className="font-semibold">{t.birthPlace}:</span> {formData.birth_place}</div>}
                    {formData.residence_city && <div><span className="font-semibold">{t.residenceCity}:</span> {formData.residence_city}</div>}
                    {formData.residence_address && <div><span className="font-semibold">{t.residenceAddress}:</span> {formData.residence_address}</div>}
                  </div>

                  <div className="border-t pt-2 mt-2 space-y-1">
                    <p className="font-semibold text-gray-800 text-[11px]">{t.declarationsTitle}</p>
                    <div className="flex items-center gap-1.5">
                      <span className="text-green-600">&#10003;</span>
                      <span className="text-[10px]">{previewLabels[language]?.health || previewLabels.it.health}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-green-600">&#10003;</span>
                      <span className="text-[10px]">{previewLabels[language]?.regulations || previewLabels.it.regulations}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-green-600">&#10003;</span>
                      <span className="text-[10px]">{previewLabels[language]?.liability || previewLabels.it.liability}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-green-600">&#10003;</span>
                      <span className="text-[10px]">{previewLabels[language]?.privacy || previewLabels.it.privacy}</span>
                    </div>
                    {formData.accepts_media_consent && (
                      <div className="flex items-center gap-1.5">
                        <span className="text-blue-600">&#10003;</span>
                        <span className="text-[10px]">{previewLabels[language]?.media || previewLabels.it.media}</span>
                      </div>
                    )}
                    {formData.accepts_newsletter && (
                      <div className="flex items-center gap-1.5">
                        <span className="text-blue-600">&#10003;</span>
                        <span className="text-[10px]">{previewLabels[language]?.newsletter || previewLabels.it.newsletter}</span>
                      </div>
                    )}
                  </div>

                  <div className="border-t pt-2 mt-2">
                    <p className="font-semibold text-gray-800 text-[11px]">{previewLabels[language]?.signature || previewLabels.it.signature}</p>
                    <div className="h-12 border border-dashed border-gray-300 rounded mt-1 flex items-center justify-center">
                      <span className="text-[10px] text-gray-400 italic">{previewLabels[language]?.signaturePlaceholder || previewLabels.it.signaturePlaceholder}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Signature */}
              <h3 className="font-semibold text-gray-900 flex items-center gap-2 pt-2">
                <Pen className="w-5 h-5" />
                {t.signTitle}
              </h3>
              <p className="text-sm text-gray-600">{t.signatureInstructions}</p>

              <div className="relative">
                <canvas
                  ref={canvasRef}
                  width={350}
                  height={200}
                  className="w-full border-2 border-dashed border-gray-300 rounded-lg bg-white touch-none cursor-crosshair"
                  onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={stopDrawing} onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing} onTouchMove={draw} onTouchEnd={stopDrawing}
                  data-testid="member-signature-canvas"
                />
                {!hasSignature && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <p className="text-gray-400">{t.signHere}</p>
                  </div>
                )}
              </div>

              <Button variant="outline" onClick={clearSignature} className="w-full" disabled={!hasSignature} data-testid="member-clear-signature">
                <RotateCcw className="w-4 h-4 mr-2" />
                {t.clearSignature}
              </Button>

              <div className="bg-amber-50 p-3 rounded-lg text-sm text-amber-800">
                <strong>{t.signatureNote}</strong>
              </div>
            </motion.div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex gap-3">
          {step > 1 && (
            <Button variant="outline" onClick={() => setStep(step - 1)} className="flex-1" data-testid="member-back-btn">
              {step === 3 ? t.editData : t.back}
            </Button>
          )}
          {step === 1 && (
            <Button
              onClick={() => {
                if (!formData.name || !formData.surname || !formData.email || !formData.phone) {
                  toast.error(t.errorRequired);
                  return;
                }
                setStep(2);
              }}
              className="flex-1 bg-teal-500 hover:bg-teal-600"
              disabled={!formData.name || !formData.surname || !formData.email || !formData.phone}
              data-testid="member-next-btn"
            >
              {t.next}
            </Button>
          )}
          {step === 2 && (
            <Button
              onClick={goToStep3}
              className="flex-1 bg-teal-500 hover:bg-teal-600"
              data-testid="member-preview-btn"
            >
              {t.next}
            </Button>
          )}
          {step === 3 && (
            <Button
              onClick={handleSubmit}
              className="flex-1 bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700"
              disabled={isSubmitting || !hasSignature}
              data-testid="member-submit-btn"
            >
              {isSubmitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t.sending}</>
              ) : (
                <><Send className="w-4 h-4 mr-2" />{t.confirmSubmit}</>
              )}
            </Button>
          )}
        </div>
      </motion.div>

      {/* Regulations Modal */}
      <AnimatePresence>
        {showRegulations && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
            onClick={() => setShowRegulations(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl max-w-lg w-full max-h-[80vh] overflow-hidden shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="bg-gradient-to-r from-teal-500 to-cyan-600 p-4 text-white">
                <h3 className="text-lg font-bold">{regs.title}</h3>
              </div>
              <div className="p-4 overflow-y-auto max-h-[60vh] space-y-3">
                {regs.rules.map((rule) => (
                  <div key={rule.number} className="bg-gray-50 p-3 rounded-lg flex items-start gap-3">
                    <span className="bg-teal-500 text-white w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">{rule.number}</span>
                    <p className="text-sm text-gray-700">{rule.text}</p>
                  </div>
                ))}
              </div>
              <div className="p-4 border-t bg-gray-50">
                <Button onClick={() => setShowRegulations(false)} className="w-full bg-teal-500 hover:bg-teal-600">{regs.close}</Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
